import { Input } from '@/components/ui/input';
import { useRecipientSuggestions } from '@/hooks/useRecipientSuggestions';
import { cn, useDebounceWithLoading } from '@/lib/utils';
import { useEffect, useState } from 'react';

interface RecipientAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
}

export function RecipientAutocompleteInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  disabled,
}: RecipientAutocompleteInputProps) {
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const token = value.split(/[,\n;]/).pop()?.trim() ?? '';
  const { debouncedValue: debouncedToken } = useDebounceWithLoading(token, 200);
  const suggestionsQuery = useRecipientSuggestions(debouncedToken, { enabled: focused });
  const suggestions = suggestionsQuery.data ?? [];
  const show = focused && token.length >= 1 && suggestions.length > 0;

  useEffect(() => {
    setActiveIndex(0);
  }, [token]);

  const select = (email: string): void => {
    const idx = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'), value.lastIndexOf('\n'));
    const prefix = idx >= 0 ? value.slice(0, idx + 1) : '';
    const sep = prefix && !prefix.endsWith(' ') ? ' ' : '';
    onChange(`${prefix}${sep}${email}, `);
    setActiveIndex(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!show) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      if (suggestions[activeIndex]) {
        event.preventDefault();
        select(suggestions[activeIndex].email);
      }
    } else if (event.key === 'Escape') {
      setFocused(false);
    }
  };

  return (
    <div className="relative w-full min-w-0">
      <Input
        className="w-full"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 120)}
        onKeyDown={handleKeyDown}
      />
      {show ? (
        <ul className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-auto rounded-md border bg-popover shadow-md">
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.email}>
              <button
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  select(suggestion.email);
                }}
                className={cn(
                  'flex w-full flex-col items-start px-3 py-1.5 text-left text-sm hover:bg-muted',
                  index === activeIndex && 'bg-muted'
                )}
              >
                <span className="w-full truncate">{suggestion.email}</span>
                {suggestion.displayName ? (
                  <span className="w-full truncate text-xs text-muted-foreground">
                    {suggestion.displayName}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
